/**
 * TemplateSelector Component - Dialog pentru selectarea și gestionarea template-urilor
 * 
 * Features:
 * - Lista template-uri proprii ale medicului
 * - Search după nume
 * - Toggle favorite (★)
 * - Preview template HTML
 * - Aplicare template în editor
 * - Edit/Delete template direct din dialog
 * 
 * Sortare: favorite first, apoi usage_count descrescător
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  TextField,
  IconButton,
  Tooltip,
  InputAdornment
} from '@mui/material';
import {
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Article as TemplateIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { doctorTemplatesApi, DoctorTemplate } from '../../services/doctorTemplatesApi';
import toast from 'react-hot-toast';

interface TemplateSelectorProps {
  open: boolean;
  onClose: () => void;
  onApply: (html: string, json: any) => void;
  onEdit?: (template: DoctorTemplate) => void;
}

const TemplateSelector: React.FC<TemplateSelectorProps> = ({
  open,
  onClose,
  onApply,
  onEdit
}) => {
  const [templates, setTemplates] = useState<DoctorTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<DoctorTemplate | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Load templates when dialog opens
  useEffect(() => {
    if (open) {
      loadTemplates();
    }
  }, [open]);
  
  const loadTemplates = async () => {
    setLoading(true);
    setError(null);
    setSelectedTemplate(null);
    
    try {
      const data = await doctorTemplatesApi.getTemplates();
      setTemplates(data || []);
      
      // Auto-select first favorite if exists
      const firstFavorite = data.find((t: DoctorTemplate) => t.is_favorite);
      if (firstFavorite) {
        setSelectedTemplate(firstFavorite);
      }
    } catch (err: any) {
      console.error('Error loading templates:', err);
      setError(err.response?.data?.detail || 'Nu s-au putut încărca template-urile');
    } finally {
      setLoading(false);
    }
  };
  
  const handleApply = async () => {
    if (!selectedTemplate) {
      toast.error('Selectează un template');
      return;
    }
    
    try {
      // Incrementează usage count
      await doctorTemplatesApi.incrementUsage(selectedTemplate.id);
      
      // Aplică template
      onApply(selectedTemplate.content_html, selectedTemplate.content_json);
      toast.success(`Template "${selectedTemplate.name}" aplicat`);
      handleClose();
    } catch (err) {
      console.error('Error applying template:', err);
      // Aplică oricum template-ul chiar dacă usage count nu s-a incrementat
      onApply(selectedTemplate.content_html, selectedTemplate.content_json);
      handleClose();
    }
  };
  
  const handleToggleFavorite = async (template: DoctorTemplate, e: React.MouseEvent) => {
    e.stopPropagation();
    
    try {
      const updated = await doctorTemplatesApi.toggleFavorite(template.id);
      setTemplates(prev => prev.map(t => t.id === updated.id ? updated : t));
      
      if (selectedTemplate?.id === updated.id) {
        setSelectedTemplate(updated);
      }
      
      toast.success(updated.is_favorite ? 'Adăugat la favorite' : 'Eliminat din favorite');
    } catch (err: any) {
      console.error('Error toggling favorite:', err);
      toast.error('Eroare la actualizarea favoritelor');
    }
  };
  
  const handleDelete = async (template: DoctorTemplate, e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (!window.confirm(`Sigur vrei să ștergi template-ul "${template.name}"?`)) {
      return;
    }
    
    try {
      await doctorTemplatesApi.deleteTemplate(template.id);
      setTemplates(prev => prev.filter(t => t.id !== template.id));
      
      if (selectedTemplate?.id === template.id) {
        setSelectedTemplate(null);
      }
      
      toast.success('Template șters');
    } catch (err: any) {
      console.error('Error deleting template:', err);
      toast.error(err.response?.data?.detail || 'Eroare la ștergerea template-ului');
    }
  };
  
  const handleEdit = (template: DoctorTemplate, e: React.MouseEvent) => {
    e.stopPropagation();
    if (onEdit) {
      onEdit(template);
      handleClose();
    }
  };
  
  const handleClose = () => {
    setSelectedTemplate(null);
    setSearchQuery('');
    onClose();
  };
  
  // Filter templates by search query
  const filteredTemplates = templates.filter(t => 
    t.name.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  // Compact dialog styles
  const dialogPaperSx = {
    bgcolor: 'background.paper',
    backgroundImage: 'none',
    '& .MuiInputLabel-root': { color: 'text.secondary', fontSize: '0.85rem' },
    '& .MuiInputLabel-root.Mui-focused': { color: 'primary.main' },
    '& .MuiOutlinedInput-root': {
      fontSize: '0.875rem',
      '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
    },
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth PaperProps={{ sx: dialogPaperSx }}>
      <DialogTitle sx={{ py: 1.5, px: 2, fontSize: '1rem', fontWeight: 600, borderBottom: '1px solid', borderColor: 'divider', color: 'text.primary' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <TemplateIcon sx={{ fontSize: 20 }} />
          <Typography sx={{ fontSize: '1rem', fontWeight: 600 }}>Template-urile Mele</Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ p: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 1.5, py: 0.5, '& .MuiAlert-message': { fontSize: '0.78rem' } }}>{error}</Alert>
        )}
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress size={24} /></Box>
        ) : templates.length === 0 ? (
          <Alert severity="info" sx={{ py: 0.5, '& .MuiAlert-message': { fontSize: '0.78rem' } }}>
            Nu ai template-uri salvate. Poți crea template-uri din pagina Profil → Template-uri sau folosind butonul "Salvează ca Template" din editor.
          </Alert>
        ) : (
          <Box sx={{ display: 'flex', gap: 2, height: 450 }}>
            {/* Template list */}
            <Box sx={{ width: '45%', display: 'flex', flexDirection: 'column' }}>
              {/* Search */}
              <TextField
                size="small"
                placeholder="Caută template..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                fullWidth
                sx={{ mb: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  )
                }}
              />
              
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                {filteredTemplates.length} template-uri
              </Typography>
              
              <List dense sx={{ flex: 1, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                {filteredTemplates.map((template) => (
                  <ListItem 
                    key={template.id} 
                    disablePadding
                    secondaryAction={
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title={template.is_favorite ? "Elimină din favorite" : "Adaugă la favorite"}>
                          <IconButton 
                            size="small" 
                            onClick={(e) => handleToggleFavorite(template, e)}
                          >
                            {template.is_favorite ? (
                              <StarIcon fontSize="small" color="warning" />
                            ) : (
                              <StarBorderIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                        {onEdit && (
                          <Tooltip title="Editează">
                            <IconButton 
                              size="small" 
                              onClick={(e) => handleEdit(template, e)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Șterge">
                          <IconButton 
                            size="small" 
                            onClick={(e) => handleDelete(template, e)}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    }
                  >
                    <ListItemButton
                      selected={selectedTemplate?.id === template.id}
                      onClick={() => setSelectedTemplate(template)}
                      sx={{ pr: 12 }}
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" noWrap>
                              {template.name}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            {template.usage_count} utilizări
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
                
                {filteredTemplates.length === 0 && searchQuery && (
                  <ListItem>
                    <ListItemText
                      primary={
                        <Typography variant="body2" color="text.secondary" align="center">
                          Nu s-au găsit template-uri pentru "{searchQuery}"
                        </Typography>
                      }
                    />
                  </ListItem>
                )}
              </List>
            </Box>
            
            {/* Template preview */}
            <Box sx={{ width: '55%', display: 'flex', flexDirection: 'column' }}>
              {selectedTemplate ? (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="subtitle2">
                      Preview: {selectedTemplate.name}
                    </Typography>
                    {selectedTemplate.is_favorite && (
                      <StarIcon fontSize="small" color="warning" />
                    )}
                  </Box>
                  <Divider sx={{ mb: 1 }} />
                  <Box
                    sx={{
                      flex: 1,
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: 2,
                      overflow: 'auto',
                      bgcolor: 'background.default',
                      '& h1, & h2, & h3': {
                        margin: '0.5em 0'
                      },
                      '& p': {
                        margin: '0.25em 0'
                      }
                    }}
                    dangerouslySetInnerHTML={{ __html: selectedTemplate.content_html }}
                  />
                </>
              ) : (
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  height: '100%',
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  bgcolor: 'background.default'
                }}>
                  <Typography variant="body2" color="text.secondary">
                    Selectează un template pentru a vedea preview
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button onClick={handleClose} size="small" sx={{ fontSize: '0.78rem', textTransform: 'none' }}>Anulează</Button>
        <Button onClick={handleApply} variant="contained" disabled={!selectedTemplate || loading} size="small" sx={{ fontSize: '0.78rem', textTransform: 'none' }}>Aplică Template</Button>
      </DialogActions>
    </Dialog>
  );
};

export default TemplateSelector;
